import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import MUIStepper from '@mui/material/Stepper';
import React, { useCallback, useMemo } from 'react';
import { H } from '../../common/Layout';
import I18n from '../i18n/I18n';
import { getActionButtons } from '../utils/commonUtils';
import { ActionType } from './ActionType';
import Loading from './Loading';
import { useStepper } from './StepperContext';
import StyledStepLabel from './StyledStepLabel';

export interface StepItem<TProps = any> {
    label: string;
    component: React.ComponentType<TProps>;
    props?: TProps;
    isOptional?: boolean;
}

interface CustomStepperProps {
    steps: StepItem[];
    onSave?: (data: any) => Promise<unknown>;
    additionalActions?: ActionType[];
    hideDefaultNextButton?: boolean;
    showLegend?: boolean;
    legendComponent?: React.ComponentType;
    customNextButtonLabelToken?: string;
    /** Blocks navigation and submission without unmounting the current step. */
    disableNavigation?: boolean;
}

export const StepsIndexConstant = {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4
};

export default function Stepper({
    steps,
    onSave,
    additionalActions = [],
    hideDefaultNextButton = false,
    showLegend = false,
    legendComponent = undefined,
    customNextButtonLabelToken = undefined,
    disableNavigation = false
}: CustomStepperProps) {
    const {
        activeStep,
        goToNextStep,
        goToPreviousStep,
        checkIsStepComplete,
        setIsSaveLoading,
        getIsSaveLoading,
        getAllStepData,
        disableDefaultNextAction,
        isEditMode,
        setStepCompletion,
        nextActionOverride,
        hideSkipButton
    } = useStepper();

    const isLastStep = activeStep === steps.length - 1;
    const isSaveOrNextDisabled =
        disableNavigation ||
        (nextActionOverride ? !!nextActionOverride.disabled : !checkIsStepComplete(activeStep)) ||
        getIsSaveLoading();
    const isCurrentStepOptional = steps[activeStep]?.isOptional || false;
    const showSkipButton = isCurrentStepOptional && !hideSkipButton && !hideDefaultNextButton;
    const customActions = (additionalActions.length && getActionButtons(additionalActions, 'stepper')) || [];
    const nextButtonLabel =
        nextActionOverride?.labelToken || customNextButtonLabelToken || `dialog.${isLastStep ? 'save' : 'next'}`;

    const CurrentStepComponent = useMemo(() => {
        return steps[activeStep].component;
    }, [activeStep, steps]);

    const LegendComponent = useMemo(() => {
        return legendComponent;
    }, [legendComponent]);

    const handleSave = useCallback(() => {
        // Collect all step data
        const allStepData = getAllStepData(steps);

        //ToDo: update based on the component usage
        if (allStepData) {
            setIsSaveLoading(true);
            onSave(allStepData).finally(() => setIsSaveLoading(false));
        }
    }, [getAllStepData, steps, onSave, setIsSaveLoading]);

    const handleNextClick = useCallback(() => {
        if (disableNavigation) return;
        if (nextActionOverride) {
            nextActionOverride.onClick();
        } else if (isLastStep) {
            handleSave();
        } else if (!disableDefaultNextAction) {
            goToNextStep();
        }
    }, [disableNavigation, nextActionOverride, isLastStep, disableDefaultNextAction, goToNextStep, handleSave]);

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: '0 0 auto' }}>
                <MUIStepper activeStep={activeStep} sx={{ p: 2 }}>
                    {steps.map((step, index) => {
                        return (
                            <Step key={step.label}>
                                <StyledStepLabel index={index} activeStep={activeStep}>
                                    {activeStep === index ? (
                                        <Typography fontWeight={'fontWeightBold'}>
                                            <I18n token={step.label} />
                                        </Typography>
                                    ) : (
                                        <></>
                                    )}
                                </StyledStepLabel>
                            </Step>
                        );
                    })}
                </MUIStepper>
            </Box>
            <Box
                sx={{
                    flex: '1 1 auto',
                    overflowY: 'auto'
                }}
            >
                {getIsSaveLoading() ? <Loading /> : <CurrentStepComponent {...steps[activeStep].props} />}
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
                <H sx={{ justifyContent: 'space-between' }}>
                    <Button
                        color="inherit"
                        disabled={activeStep === 0 || disableNavigation}
                        onClick={goToPreviousStep}
                        sx={{ m: 1, color: 'text.primary' }}
                    >
                        <I18n token="dialog.back" />
                    </Button>
                    {showLegend && (
                        <H sx={{ width: '65%' }}>
                            <LegendComponent />
                        </H>
                    )}
                    <H sx={{ width: 'unset', justifyContent: 'flex-start', mr: 2 }}>
                        {...customActions}
                        {showSkipButton && (
                            <Button
                                onClick={() => {
                                    setStepCompletion(activeStep, true);
                                    goToNextStep();
                                }}
                                disabled={disableNavigation || getIsSaveLoading()}
                                variant="outlined"
                                sx={{ m: 1 }}
                            >
                                <I18n token="dialog.skip" />
                            </Button>
                        )}
                        {!hideDefaultNextButton && (
                            <Button
                                onClick={handleNextClick}
                                disabled={isSaveOrNextDisabled}
                                variant={isEditMode && !isLastStep ? 'outlined' : 'contained'}
                                sx={{ m: 1 }}
                            >
                                <I18n token={nextButtonLabel} />
                            </Button>
                        )}
                    </H>
                </H>
            </Box>
        </Box>
    );
}
